import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Film, ArrowLeft, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DyvysAdminSeriesPage() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    cover_image_url: "",
    category: ""
  });
  const navigate = useNavigate();

  const adminPassword = localStorage.getItem("dyvys_admin_password");

  useEffect(() => {
    if (!adminPassword) {
      toast.error("Veuillez vous connecter");
      navigate("/dyvys/admin/login");
      return;
    }
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dyvys/admin/series`, {
        headers: {
          "X-Admin-Password": adminPassword
        }
      });

      if (!response.ok) {
        throw new Error("Accès refusé");
      }

      const data = await response.json();
      setSeries(data.series || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur de chargement");
      if (error.message === "Accès refusé") {
        localStorage.removeItem("dyvys_admin_password");
        navigate("/dyvys/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (seriesItem = null) => {
    if (seriesItem) {
      setEditingSeries(seriesItem);
      setFormData({
        title: seriesItem.title,
        description: seriesItem.description || "",
        cover_image_url: seriesItem.cover_image_url || "",
        category: seriesItem.category || ""
      });
    } else {
      setEditingSeries(null);
      setFormData({
        title: "",
        description: "",
        cover_image_url: "",
        category: ""
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingSeries
        ? `${API_URL}/api/dyvys/admin/series/${editingSeries.series_id}`
        : `${API_URL}/api/dyvys/admin/series`;

      const method = editingSeries ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Password": adminPassword
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error("Erreur de sauvegarde");

      toast.success(editingSeries ? "Série mise à jour!" : "Série créée!");
      setShowDialog(false);
      fetchSeries();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (seriesId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette série et tous ses épisodes ?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/dyvys/admin/series/${seriesId}`, {
        method: "DELETE",
        headers: {
          "X-Admin-Password": adminPassword
        }
      });

      if (!response.ok) throw new Error("Erreur de suppression");

      toast.success("Série supprimée!");
      fetchSeries();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("dyvys_admin_password");
    navigate("/dyvys/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-purple-800/50 backdrop-blur-sm bg-black/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">DYVY'S Afrique - Admin</h1>
              <p className="text-gray-400">Gestion des Séries</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate("/dyvys")}
                variant="outline"
                className="border-purple-500 text-purple-300 hover:bg-purple-500/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Site Public
              </Button>
              <Button
                onClick={handleLogout}
                variant="destructive"
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Actions */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold">Toutes les Séries ({series.length})</h2>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Série
          </Button>
        </div>

        {/* Series Grid */}
        {series.length === 0 ? (
          <div className="text-center py-20">
            <Film className="w-16 h-16 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-xl text-gray-400">Aucune série créée</p>
            <p className="text-gray-500 mt-2">Cliquez sur "Nouvelle Série" pour commencer</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {series.map((item) => (
              <Card
                key={item.series_id}
                className="bg-gradient-to-br from-gray-800/50 to-purple-900/30 border-purple-500/30"
              >
                <CardHeader className="p-0">
                  {item.cover_image_url ? (
                    <img
                      src={item.cover_image_url}
                      alt={item.title}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center rounded-t-lg">
                      <Film className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  <CardTitle className="text-lg text-white mb-2">{item.title}</CardTitle>
                  {item.category && (
                    <div className="inline-block bg-purple-500/20 text-purple-300 text-xs px-3 py-1 rounded-full mb-2">
                      {item.category}
                    </div>
                  )}
                  <CardDescription className="text-gray-400 text-sm line-clamp-2">
                    {item.description || "Aucune description"}
                  </CardDescription>
                  <div className="mt-3 text-sm text-gray-500">
                    {item.is_active ? (
                      <span className="text-green-400">● Active</span>
                    ) : (
                      <span className="text-red-400">● Inactive</span>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="p-4 pt-0 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-purple-500 text-purple-300 hover:bg-purple-500/20"
                    onClick={() => navigate(`/dyvys/admin/episodes/${item.series_id}`)}
                  >
                    <BarChart3 className="w-4 h-4 mr-1" />
                    Épisodes
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-500 text-blue-300 hover:bg-blue-500/20"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-300 hover:bg-red-500/20"
                    onClick={() => handleDelete(item.series_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 text-white border-purple-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingSeries ? "Modifier la série" : "Nouvelle série"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingSeries ? "Modifiez les informations de la série" : "Créez une nouvelle série de dramas"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre de la série *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Catégorie</Label>
                <Input
                  id="category"
                  placeholder="Ex: Action, Romance, Drame..."
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={4}
                  placeholder="Décrivez la série..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cover">URL de l'image de couverture</Label>
                <Input
                  id="cover"
                  type="url"
                  placeholder="https://..."
                  value={formData.cover_image_url}
                  onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                />
                <p className="text-xs text-gray-500">
                  L'image doit être hébergée en ligne (ex: Facebook, Imgur, etc.)
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-gray-600 text-gray-300"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {editingSeries ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
